import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

const SmartRedirect = () => {
  const { user, hasPermission, isLoading, isAuthenticated, syncSession } = useAuth();
  const hasTriedSyncRef = useRef(false);

  // Hooks devem ser chamados incondicionalmente.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (user && Array.isArray(user.permissions)) return;
    if (hasTriedSyncRef.current) return;

    const hasToken = !!localStorage.getItem('token');
    if (!hasToken) return;

    hasTriedSyncRef.current = true;
    syncSession({ silent: true });
  }, [isLoading, isAuthenticated, syncSession, user]);

  // Definir hierarquia de páginas baseada nas permissões
  const pageHierarchy = [
    {
      path: '/dashboard',
      permission: 'analytics.dashboard.read',
      label: 'Dashboard Analytics'
    },
    {
      path: '/admin/finance/analytics',
      permission: 'analytics.finance.read',
      label: 'Analytics Financeiro'
    },
    {
      path: '/admin/inventory/analytics',
      permission: 'analytics.inventory.read',
      label: 'Analytics de Inventário'
    },
    {
      path: '/admin/analytics/manual-sales-performance',
      permission: 'analytics.manual_sales_performance.read',
      label: 'Análises • Desempenho Manual'
    },
    {
      path: '/admin/users',
      permission: 'users.read',
      label: 'Gerenciamento de Usuários'
    },
    {
      path: '/admin/roles',
      permission: 'roles.read',
      label: 'Gerenciamento de Roles'
    },
    {
      path: '/admin/finance/transactions',
      // Considerar ALL ou SELF como acesso válido
      permission: 'finance.transactions.read.all',
      label: 'Gestão Financeira'
    },
    {
      path: '/admin/assets',
      permission: 'inventory.assets.read',
      label: 'Controle de Inventário'
    }
  ];

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <CircularProgress size={60} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Verificando permissões...
        </Typography>
      </Box>
    );
  }

  if (isAuthenticated && (!user || !Array.isArray(user.permissions))) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <CircularProgress size={60} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Sincronizando sessão...
        </Typography>
      </Box>
    );
  }

  // Encontrar a primeira página que o usuário tem permissão
  const firstAvailablePage = pageHierarchy.find(page => {
    if (page.path === '/admin/finance/transactions') {
      return hasPermission('finance.transactions.read.all') || hasPermission('finance.transactions.read.self');
    }
    return hasPermission(page.permission);
  });

  // Se não encontrar nenhuma página com permissão, verificar se é admin
  if (!firstAvailablePage) {
    // Se for admin mas não tem permissões específicas, redirecionar para dashboard
    if (user?.is_admin) {
      return <Navigate to="/dashboard" replace />;
    }
    
    // Se não tem nenhuma permissão, mostrar mensagem de erro
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        textAlign="center"
        sx={{ p: 3 }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Acesso Negado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Você não possui permissões para acessar nenhuma página do sistema.
          Entre em contato com o administrador.
        </Typography>
      </Box>
    );
  }

  // Redirecionar para a primeira página disponível
  return <Navigate to={firstAvailablePage.path} replace />;
};

export default SmartRedirect;